'use client';

import { CornerDownLeftIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/corner-down-left-icon';
import { ExternalLinkIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/external-link-icon';
import { LinkIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/link-icon';
import { TrashIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/trash-icon';
import type { ButtonProps } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/button';
import { Button, ButtonGroup } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/button';
import {
  Card,
  CardBody,
  CardItemGroup,
} from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/card';
import { Input, InputGroup } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/popover';
import { Separator } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/separator';
import type { UseLinkPopoverConfig } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/link-popover';
import { useLinkPopover } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/link-popover';
import {
  SmartLinkDropdownPanel,
  SmartLinkToggleButton,
  useSmartLinkDropdown,
} from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/link-popover/smart-link-dropdown';
import type { SmartLinkOption } from '@examdojo/core/tiptap-react-ui-components/contexts/smart-link-context';
import { useSmartLink } from '@examdojo/core/tiptap-react-ui-components/contexts/smart-link-context';
import { isExternalUrl } from '@examdojo/core/util/url-utils';
import { useIsBreakpoint } from '@examdojo/core/tiptap-react-ui-components/hooks/use-is-breakpoint';
import { useTiptapEditor } from '@examdojo/core/tiptap-react-ui-components/hooks/use-tiptap-editor';
import type { Editor } from '@tiptap/react';
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';

export interface LinkMainProps {
  /**
   * The URL to set for the link.
   */
  url: string;
  /**
   * Function to update the URL state.
   */
  setUrl: React.Dispatch<React.SetStateAction<string | null>>;
  /**
   * Function to set the link in the editor.
   */
  setLink: () => void;
  /**
   * Function to remove the link from the editor.
   */
  removeLink: () => void;
  /**
   * Function to open the link.
   */
  openLink: () => void;
  /**
   * Whether the link is currently active in the editor.
   */
  isActive: boolean;
}

export interface LinkPopoverProps extends Omit<ButtonProps, 'type'>, UseLinkPopoverConfig {
  /**
   * Callback for when the popover opens or closes.
   */
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * Whether to automatically open the popover when a link is active.
   * @default true
   */
  autoOpenOnLinkActive?: boolean;
}

/**
 * Link button component for triggering the link popover
 */
export const LinkButton = forwardRef<HTMLButtonElement, ButtonProps>(({ className, children, ...props }, ref) => {
  return (
    <Button
      type="button"
      className={className}
      data-style="ghost"
      role="button"
      tabIndex={-1}
      aria-label="Link"
      tooltip="Link"
      ref={ref}
      {...props}
    >
      {children || <LinkIcon className="tiptap-button-icon" />}
    </Button>
  );
});

LinkButton.displayName = 'LinkButton';

/**
 * Main content component for the link popover
 */
const LinkMain: React.FC<LinkMainProps> = ({ url, setUrl, setLink, removeLink, openLink, isActive }) => {
  const isMobile = useIsBreakpoint();
  const smartLink = useSmartLink();
  const [matchedLabel, setMatchedLabel] = useState<string | null>(null);
  const [cachedOptions, setCachedOptions] = useState<SmartLinkOption[]>([]);

  useEffect(() => {
    if (!smartLink?.getOptions) return;
    smartLink.getOptions().then((opts) => setCachedOptions(opts));
  }, [smartLink]);

  const currentMatchedLabel = useMemo(() => {
    if (matchedLabel) return matchedLabel;
    if (!url || cachedOptions.length === 0) return null;
    return cachedOptions.find((opt) => opt.value === url)?.label ?? null;
  }, [url, matchedLabel, cachedOptions]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      setLink();
    }
  };

  const handleOptionSelect = useCallback(
    (option: SmartLinkOption) => {
      setUrl(option.value);
      setMatchedLabel(option.label);
    },
    [setUrl],
  );

  const dropdown = useSmartLinkDropdown({
    getOptions: smartLink?.getOptions,
    onSelect: handleOptionSelect,
  });

  const handleOpenLink = useCallback(() => {
    if (!url) return;
    if (smartLink?.resolve && !isExternalUrl(url)) {
      smartLink.resolve(url, { url: window.location.href });
      return;
    }
    openLink();
  }, [url, smartLink, openLink]);

  return (
    <Card
      style={{
        ...(isMobile ? { boxShadow: 'none', border: 0 } : {}),
      }}
    >
      <CardBody
        style={{
          ...(isMobile ? { padding: 0 } : {}),
        }}
      >
        <CardItemGroup orientation="horizontal">
          {currentMatchedLabel && (
            <span title={currentMatchedLabel} className="tiptap-smart-link-matched-icon">
              <LinkIcon className="tiptap-button-icon" />
            </span>
          )}

          <InputGroup>
            <Input
              type="url"
              placeholder={dropdown.enabled ? 'Paste a link or identifier...' : 'Paste a link...'}
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                setMatchedLabel(null);
              }}
              onKeyDown={handleKeyDown}
              autoFocus
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
            />
          </InputGroup>

          {dropdown.enabled && <SmartLinkToggleButton isOpen={dropdown.isOpen} onToggle={dropdown.toggle} />}

          <ButtonGroup orientation="horizontal">
            <Button type="button" onClick={setLink} title="Apply link" disabled={!url && !isActive} data-style="ghost">
              <CornerDownLeftIcon className="tiptap-button-icon" />
            </Button>
          </ButtonGroup>

          <Separator />

          <ButtonGroup orientation="horizontal">
            <Button
              type="button"
              onClick={handleOpenLink}
              title="Open link"
              disabled={!url && !isActive}
              data-style="ghost"
            >
              <ExternalLinkIcon className="tiptap-button-icon" />
            </Button>

            <Button
              type="button"
              onClick={removeLink}
              title="Remove link"
              disabled={!url && !isActive}
              data-style="ghost"
            >
              <TrashIcon className="tiptap-button-icon" />
            </Button>
          </ButtonGroup>
        </CardItemGroup>

        {dropdown.isOpen && (
          <SmartLinkDropdownPanel
            isLoading={dropdown.isLoading}
            filter={dropdown.filter}
            setFilter={dropdown.setFilter}
            filteredOptions={dropdown.filteredOptions}
            highlightedIndex={dropdown.highlightedIndex}
            onSelect={dropdown.selectOption}
            onKeyDown={dropdown.handleFilterKeyDown}
          />
        )}
      </CardBody>
    </Card>
  );
};

/**
 * Link content component for standalone use
 */
export const LinkContent: React.FC<{
  editor?: Editor | null;
}> = ({ editor }) => {
  const linkPopover = useLinkPopover({
    editor,
  });

  return <LinkMain {...linkPopover} />;
};

/**
 * Link popover component for Tiptap editors.
 *
 * For custom popover implementations, use the `useLinkPopover` hook instead.
 */
export const LinkPopover = forwardRef<HTMLButtonElement, LinkPopoverProps>(
  (
    {
      editor: providedEditor,
      hideWhenUnavailable = false,
      onSetLink,
      onOpenChange,
      autoOpenOnLinkActive = true,
      onClick,
      children,
      ...buttonProps
    },
    ref,
  ) => {
    const { editor } = useTiptapEditor(providedEditor);
    const [isOpen, setIsOpen] = useState(false);

    const { isVisible, canSet, isActive, url, setUrl, setLink, removeLink, openLink, label, Icon } = useLinkPopover({
      editor,
      hideWhenUnavailable,
      onSetLink,
    });

    const handleOnOpenChange = useCallback(
      (nextIsOpen: boolean) => {
        setIsOpen(nextIsOpen);
        onOpenChange?.(nextIsOpen);
      },
      [onOpenChange],
    );

    const handleSetLink = useCallback(() => {
      setLink();
      setIsOpen(false);
    }, [setLink]);

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        setIsOpen(!isOpen);
      },
      [onClick, isOpen],
    );

    useEffect(() => {
      if (autoOpenOnLinkActive && isActive) {
        setIsOpen(true);
      }
    }, [autoOpenOnLinkActive, isActive]);

    if (!isVisible) {
      return null;
    }

    return (
      <Popover open={isOpen} onOpenChange={handleOnOpenChange}>
        <PopoverTrigger asChild>
          <LinkButton
            disabled={!canSet}
            data-active-state={isActive ? 'on' : 'off'}
            data-disabled={!canSet}
            aria-label={label}
            aria-pressed={isActive}
            onClick={handleClick}
            {...buttonProps}
            ref={ref}
          >
            {children ?? <Icon className="tiptap-button-icon" />}
          </LinkButton>
        </PopoverTrigger>

        <PopoverContent>
          <LinkMain
            url={url}
            setUrl={setUrl}
            setLink={handleSetLink}
            removeLink={removeLink}
            openLink={openLink}
            isActive={isActive}
          />
        </PopoverContent>
      </Popover>
    );
  },
);

LinkPopover.displayName = 'LinkPopover';

export default LinkPopover;
